import {
  useGetDataAnalyticsBrand,
  useGetDataAnalyticsCauseOfDamage,
  useGetDataAnalyticsDamageFromDirection,
  useGetDataAnalyticsDealershipLocation,
  useGetDataAnalyticsDossierType,
  useGetDataAnalyticsDossiersPerDamageDate,
  useGetDataAnalyticsDriveable,
  useGetDataAnalyticsExternalInsured,
  useGetDataAnalyticsFleetLeaseLabelType,
  useGetDataAnalyticsGlassJobType,
  useGetDataAnalyticsGuaranteeFund,
  useGetDataAnalyticsHasImages,
  useGetDataAnalyticsHasRepairAppointment,
  useGetDataAnalyticsLeaseCompany,
  useGetDataAnalyticsMediaIntentPerDay,
  useGetDataAnalyticsOtherPartyInvolved,
  useGetDataAnalyticsOwnership,
  useGetDataAnalyticsPhotosPerDossier,
  useGetDataAnalyticsRepairRequested,
  useGetDataAnalyticsRepairType,
  useGetDataAnalyticsReplacementVehicle,
  useGetDataAnalyticsServicePartner,
  useGetDataAnalyticsServicePartnerChain,
  useGetDataAnalyticsServicePartnerPreferred,
  useGetDataAnalyticsSource,
  useGetDataAnalyticsTotalDossiers,
  useGetDataAnalyticsTowedAway,
  useGetDataAnalyticsTypeOfDamage,
  useGetDataAnalyticsVehicleAlreadyAtRepairer,
} from "@/lib/api/generated/data-analytics/data-analytics";
import {
  useGetDossiersCauseOfDamage,
  useGetDossiersDamageFromDirection,
  useGetDossiersOtherParty,
  useGetDossiersTypeOfDamage,
} from "@/lib/api/generated/dossier/dossier";
import { CalendarCheck, Camera, FileImage, FolderOpen } from "lucide-react";
import type { PhotosPerDossierItemOutput } from "@/lib/api/generated/model";
import type { ChartSegment, ChartTimeSeriesPoint } from "@rumbledirect/ui-v2";
import { DOSSIER_TYPE_LABELS } from "@/dossiers/dossierTypeLabels";
import { REPAIR_TYPE_LABELS } from "@/dossiers/repairTypeLabels";
import {
  parseISODate,
  subCalendarDays,
  toISODate,
} from "@rumbledirect/ui-v2/day";
import {
  type AnalyticsParams,
  type NON_BREAKDOWN_SECTIONS,
  toBreakdownEntries,
  toTotalDossiersCount,
} from "@/reporting/analytics";
import type {
  ReportingKpi,
  ReportingSection,
} from "@/reporting/reportingLayout";

/**
 * The chart registry: maps each chartable `ReportingSection` to its Dutch
 * title, the generated `data-analytics` query hook, and which chart primitive
 * renders it. Chart-type choices follow the design-lab prototype
 * (`LedgerReportsModule`): donuts for small yes/no/unknown style breakdowns,
 * bar lists for many-valued categoricals, ranked lists for "top N" lists, and
 * a trend bar chart for the per-day series.
 *
 * `useQuery` is deliberately NOT a union of the generated hooks: they are
 * structurally identical (`params` in, response out, and `select` applies),
 * and collapsing them here keeps one widening away per new endpoint. Every
 * call site looks its entry up per component instance (keyed by section), so
 * the hook identity stays stable under a stable instance.
 *
 * The sections listed in `NON_BREAKDOWN_SECTIONS` (`analytics.ts`) are
 * deliberately absent: their responses are not breakdowns, and the key type
 * `Exclude<ReportingSection, NonBreakdownSection>` makes wiring one of them
 * up here a compile error rather than a silent gap — whoever adds its
 * renderer also removes it from the guard list. The page skips a section the
 * registry does not cover.
 */

type NonBreakdownSection = (typeof NON_BREAKDOWN_SECTIONS)[number];

/** The `data-analytics` sections the chart registry covers. */
export type ReportingChartSection = Exclude<
  ReportingSection,
  NonBreakdownSection | "dossierKpis" | "statusBreakdown"
>;

/** The breakdown sections whose keys are simply "yes" / "no". */
const YES_NO_LABELS: Readonly<Record<string, string>> = {
  yes: "Ja",
  no: "Nee",
};

/**
 * The keys under which the endpoints group "not recorded" values — and they
 * do not agree on one: `driveable` and `service-partner-chain` serve `"-"`,
 * `source` and `brand` serve `""` (verified against the live API). Treat
 * either as the same bucket rather than keying on exactly one: the other
 * sentinel would render as a blank-labelled, unmuted bar sorted first.
 */
const NOT_RECORDED_KEYS: ReadonlySet<string> = new Set(["-", ""]);
const NOT_RECORDED_LABEL = "Niet vastgelegd";

/**
 * Labels for the raw breakdown keys the endpoints return. Ported from admin
 * v1's `frontend/admin/src/lib/formatter/dossiers.tsx` and the inline maps in
 * its `AnalyticsChartsGrid.tsx`: neither admin-v2 nor `@rumble-it/types` has
 * a label source for these values. An unmapped key falls back to itself
 * rather than rendering blank, so a new backend value degrades to a raw key
 * instead of an empty label. Enum-keyed charts (`causeOfDamage`,
 * `otherPartyInvolved`) do not use these maps — their labels come from the
 * backend enum collections via `useLabels` (see below).
 */
const SOURCE_LABELS: Readonly<Record<string, string>> = {
  "dealer-intake": "Dealer Intake",
  chatbot: "Chatbot",
  widget: "Widget",
  "mobile-app": "Mobiele app",
  website: "Website",
};

const OWNERSHIP_LABELS: Readonly<Record<string, string>> = {
  privatelyOwned: "Particulier",
  leaseCompanyOwned: "Lease",
};

const SERVICE_PARTNER_PREFERRED_LABELS: Readonly<Record<string, string>> = {
  preferred: "Preferred partner",
  not_preferred: "Agreed partner",
};

const FLEET_LEASE_LABEL_TYPE_LABELS: Readonly<Record<string, string>> = {
  b2b: "Zakelijk",
  b2c: "Particulier",
};

/**
 * `GlassJobType` has no enum collection either; translated the same way from
 * `translations/messages.nl.yaml`'s `GlassJobType` block.
 */
const GLASS_JOB_TYPE_LABELS: Readonly<Record<string, string>> = {
  repair: "Herstellen",
  replace: "Vervangen",
};

/** The shape every generated `useGetDataAnalytics*` hook shares. */
interface AnalyticsQueryResult<T> {
  readonly data: T | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => Promise<unknown>;
}

/**
 * The request options a generated `useGetDataAnalytics*` hook's `options.request`
 * slot takes — the mutator's second parameter, without importing the
 * (mutator-internal) `customInstance` symbol. The page passes
 * `ANALYTICS_REQUEST_OPTIONS` here: the endpoints are plain JSON, so the
 * client's JSON-LD `Accept` default must be overridden per call (see the
 * constant's own comment).
 */
export interface AnalyticsRequestOptions {
  readonly headers?: Readonly<Record<string, string>>;
}

/**
 * The page-side call signature of a generated `useGetDataAnalytics*` hook,
 * keeping its own params type: params in, the query result out. The
 * generated hooks are overloaded (defined-data/undefined-data variants), so
 * this pins the two-argument call shape the page actually uses (the
 * `options.query` slots stay open: the KPI entry's exact generated signature
 * covers `enabled`). A trend entry's `useQuery` uses this alias too now
 * (see {@link TrendChartEntry}): the one hook whose params don't naturally
 * fit it (`media-intent-per-day`, which needs a fixed `intent`) is wrapped
 * by {@link useDamagePhotosPerDayTrend} so it does.
 */
type AnalyticsQueryHook<P> = (
  params: P,
  options?: { readonly request?: AnalyticsRequestOptions },
) => AnalyticsQueryResult<unknown>;

/**
 * Enum collections are static reference data: the labels change only with a
 * deploy, so a long staleTime keeps tab and period switches from refetching.
 */
export const ENUM_LABEL_STALE_TIME_MS = 60 * 60 * 1000;

/**
 * Reads the served enum collection into a `value -> label` map. Members
 * without a string `value`/`label` pair are skipped — a key missing from the
 * map falls back to the raw key in `toChartSegments`, which beats rendering a
 * blank label. The OpenAPI schema of these resources declares only
 * `name`/`value`; the `label` is appended at runtime by the backend's
 * `BackedEnumTranslatable` normalizer, so it is re-narrowed here rather than
 * trusted from the generated type.
 */
export function toEnumLabelMap(
  payload: unknown,
): Readonly<Record<string, string>> {
  if (payload === null || typeof payload !== "object") {
    return {};
  }
  const member = (payload as { member?: unknown }).member;
  if (!Array.isArray(member)) {
    return {};
  }

  const map: Record<string, string> = {};
  for (const item of member) {
    if (item === null || typeof item !== "object") {
      continue;
    }
    const { value, label } = item as { value?: unknown; label?: unknown };
    if (typeof value === "string" && typeof label === "string") {
      map[value] = label;
    }
  }
  return map;
}

/**
 * The CauseOfDamage enum collection as a `value -> label` map. The
 * `data-analytics/cause-of-damage` endpoint returns raw enum keys (the
 * generated client's docblock points here for their translations). The
 * generated query key is stable, so the two charts on the same tab share one
 * cached request.
 */
function useCauseOfDamageLabels(): LabelQueryResult {
  const query = useGetDossiersCauseOfDamage({
    query: { staleTime: ENUM_LABEL_STALE_TIME_MS },
  });
  return {
    data: query.data === undefined ? undefined : toEnumLabelMap(query.data),
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** The OtherParty enum collection as a `value -> label` map — same pattern. */
function useOtherPartyLabels(): LabelQueryResult {
  const query = useGetDossiersOtherParty({
    query: { staleTime: ENUM_LABEL_STALE_TIME_MS },
  });
  return {
    data: query.data === undefined ? undefined : toEnumLabelMap(query.data),
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** The TypeOfDamage enum collection as a `value -> label` map — same pattern. */
function useTypeOfDamageLabels(): LabelQueryResult {
  const query = useGetDossiersTypeOfDamage({
    query: { staleTime: ENUM_LABEL_STALE_TIME_MS },
  });
  return {
    data: query.data === undefined ? undefined : toEnumLabelMap(query.data),
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** The DamageFromDirection enum collection as a `value -> label` map — same pattern. */
function useDamageFromDirectionLabels(): LabelQueryResult {
  const query = useGetDossiersDamageFromDirection({
    query: { staleTime: ENUM_LABEL_STALE_TIME_MS },
  });
  return {
    data: query.data === undefined ? undefined : toEnumLabelMap(query.data),
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export interface BaseChartEntry {
  readonly title: string;
}

/**
 * The query result of a chart entry's `useLabels` hook: the resolved
 * `value -> label` map of a backend enum collection, plus the query state the
 * card folds into its own pending/error handling. Shaped like
 * `AnalyticsQueryResult` so both fold into a card identically.
 */
export type LabelQueryResult = AnalyticsQueryResult<
  Readonly<Record<string, string>>
>;

export interface BreakdownChartEntry extends BaseChartEntry {
  readonly chart:
    | { readonly kind: "donut" }
    | { readonly kind: "barList"; readonly variant: "bar" | "ranked" };
  readonly useQuery: AnalyticsQueryHook<AnalyticsParams>;
  /** Translates raw endpoint keys; defaults to "the key is the label". */
  readonly labels?: Readonly<Record<string, string>>;
  /**
   * Resolves labels from a backend enum collection instead of the static
   * `labels` map. The card treats this query like the data query: it stays
   * pending until both resolve and errors when either fails, so raw enum keys
   * never flash on screen. Mutually exclusive with `labels`.
   */
  readonly useLabels?: () => LabelQueryResult;
}

/**
 * The per-day series. Two endpoints fit here: `dossiers-per-damage-date`,
 * which is plain range-only, and `media-intent-per-day`, which needs a fixed
 * `intent`. Rather than typing `useQuery` as a union of the two hooks' exact
 * (incompatible) signatures — which would let a future entry wire the
 * media-intent hook in without its required `intent`, type-checking fine
 * until the request itself fails — {@link useDamagePhotosPerDayTrend} below
 * closes over the fixed param, so both registry entries expose the same
 * `AnalyticsQueryHook<AnalyticsParams>` contract every other chart's
 * `useQuery` already does. No cast, no `fixedParams` field, no per-kind
 * dispatch: `ReportingChartCard` (`reporting.tsx`) calls a trend entry's
 * `useQuery` exactly like a breakdown entry's.
 */
export interface TrendChartEntry extends BaseChartEntry {
  readonly chart: { readonly kind: "trend" };
  readonly useQuery: AnalyticsQueryHook<AnalyticsParams>;
}

/**
 * Wraps `useGetDataAnalyticsMediaIntentPerDay` with its `intent` fixed to
 * `damage_overview` — the canonical damage-photo intent
 * (`MediaUsageIntent::damagePhotoIntents()` starts with it) — so the trend
 * answers "how many damage photos arrive per day" while exposing the same
 * `AnalyticsQueryHook<AnalyticsParams>` shape as every other chart entry.
 */
function useDamagePhotosPerDayTrend(
  params: AnalyticsParams,
  options?: { readonly request?: AnalyticsRequestOptions },
): AnalyticsQueryResult<unknown> {
  return useGetDataAnalyticsMediaIntentPerDay(
    { ...params, intent: "damage_overview" },
    options,
  );
}

/**
 * Photos per dossier: the response is a list of rows, not a breakdown map,
 * so the entry carries its own params and the page buckets the rows itself
 * (`toPhotoCountSegments`).
 */
export interface PhotosPerDossierChartEntry extends BaseChartEntry {
  readonly chart: { readonly kind: "photosPerDossier" };
  readonly useQuery: AnalyticsQueryHook<AnalyticsParams>;
}

export type ReportingChartEntry =
  | BreakdownChartEntry
  | TrendChartEntry
  | PhotosPerDossierChartEntry;

export const REPORTING_CHARTS: Record<
  ReportingChartSection,
  ReportingChartEntry
> = {
  mediaIntentPerDay: {
    title: "Schadefoto's per dag",
    chart: { kind: "trend" },
    useQuery: useDamagePhotosPerDayTrend,
  },
  dossiersPerDamageDate: {
    title: "Dossiers per schadedatum",
    chart: { kind: "trend" },
    // A plain range-only endpoint: no fixed param, unlike mediaIntentPerDay.
    useQuery: useGetDataAnalyticsDossiersPerDamageDate,
  },
  photosPerDossier: {
    title: "Schadefoto's per dossier",
    chart: { kind: "photosPerDossier" },
    useQuery: useGetDataAnalyticsPhotosPerDossier,
  },
  source: {
    title: "Herkomst dossiers",
    chart: { kind: "barList", variant: "bar" },
    useQuery: useGetDataAnalyticsSource,
    labels: SOURCE_LABELS,
  },
  dossierType: {
    title: "Dossiertypes",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsDossierType,
    labels: DOSSIER_TYPE_LABELS,
  },
  brand: {
    title: "Top merken",
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsBrand,
  },
  hasImages: {
    title: "Foto's aanwezig",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsHasImages,
    labels: YES_NO_LABELS,
  },
  driveable: {
    title: "Rijdbaar",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsDriveable,
    labels: YES_NO_LABELS,
  },
  towedAway: {
    title: "Afgesleept",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsTowedAway,
    labels: YES_NO_LABELS,
  },
  replacementVehicle: {
    title: "Vervangend vervoer",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsReplacementVehicle,
    labels: YES_NO_LABELS,
  },
  vehicleAlreadyAtRepairer: {
    title: "Voertuig al bij hersteller",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsVehicleAlreadyAtRepairer,
    labels: YES_NO_LABELS,
  },
  causeOfDamage: {
    title: "Schadeoorzaak",
    chart: { kind: "barList", variant: "bar" },
    useQuery: useGetDataAnalyticsCauseOfDamage,
    // The endpoint returns raw CauseOfDamage enum keys; the translated labels
    // live on the backend's CauseOfDamage enum resource (the generated
    // client's own docblock says so), so they are read from there.
    useLabels: useCauseOfDamageLabels,
  },
  typeOfDamage: {
    title: "Schadegrootte",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsTypeOfDamage,
    useLabels: useTypeOfDamageLabels,
  },
  damageFromDirection: {
    title: "Schadelocatie",
    // ~25 possible directions (see backend `DamageFromDirection`), too many
    // for a donut to read; a ranked bar list handles that count the way
    // `brand`/`dealershipLocation` already do.
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsDamageFromDirection,
    useLabels: useDamageFromDirectionLabels,
  },
  otherPartyInvolved: {
    title: "Tegenpartij betrokken",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsOtherPartyInvolved,
    useLabels: useOtherPartyLabels,
  },
  guaranteeFund: {
    title: "Waarborgfonds",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsGuaranteeFund,
    labels: YES_NO_LABELS,
  },
  externalInsured: {
    title: "Extern verzekerd",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsExternalInsured,
    labels: YES_NO_LABELS,
  },
  hasRepairAppointment: {
    title: "Herstelafspraak",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsHasRepairAppointment,
    labels: YES_NO_LABELS,
  },
  repairRequested: {
    title: "Herstel aangevraagd",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsRepairRequested,
    labels: YES_NO_LABELS,
  },
  servicePartner: {
    title: "Top herstellers",
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsServicePartner,
  },
  servicePartnerPreferred: {
    title: "Voorkeurspartner",
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsServicePartnerPreferred,
    labels: SERVICE_PARTNER_PREFERRED_LABELS,
  },
  leaseCompany: {
    title: "Top leasebedrijven",
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsLeaseCompany,
  },
  servicePartnerChain: {
    title: "Herstelketen",
    chart: { kind: "barList", variant: "bar" },
    useQuery: useGetDataAnalyticsServicePartnerChain,
  },
  repairType: {
    title: "SMART repair",
    // RepairType currently has one case (`smart-repair`), so this is a
    // two-way split against "not recorded" rather than a real distribution —
    // see RepairTypeResource's own doc comment. Only generic RepairDirect
    // dossiers carry a repair type.
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsRepairType,
    labels: REPAIR_TYPE_LABELS,
  },
  glassJobType: {
    title: "Glas: reparatie of vervanging",
    // Only glass dossiers carry a job type; a distinct chart from repairType
    // since the two fields apply to disjoint dossier families.
    chart: { kind: "donut" },
    useQuery: useGetDataAnalyticsGlassJobType,
    labels: GLASS_JOB_TYPE_LABELS,
  },
  dealershipLocation: {
    title: "Top dealervestigingen",
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsDealershipLocation,
  },
  ownership: {
    title: "Eigendom",
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsOwnership,
    labels: OWNERSHIP_LABELS,
  },
  fleetLeaseLabelType: {
    title: "Fleet lease label",
    chart: { kind: "barList", variant: "ranked" },
    useQuery: useGetDataAnalyticsFleetLeaseLabelType,
    labels: FLEET_LEASE_LABEL_TYPE_LABELS,
  },
};

/**
 * Turns raw breakdown entries into chart segments: labels translated via the
 * entry's map (falling back to the raw key), sorted by count descending, with
 * the not-recorded buckets (`"-"` and `""` — the endpoints disagree) marked
 * `muted` and moved last so an unknown value never reads as a real outcome.
 */
export function toChartSegments(
  entries: readonly { label: string; count: number }[],
  labels?: Readonly<Record<string, string>>,
): ChartSegment[] {
  return entries
    .map(({ label, count }) => ({
      label: NOT_RECORDED_KEYS.has(label)
        ? NOT_RECORDED_LABEL
        : (labels?.[label] ?? label),
      value: count,
      muted: NOT_RECORDED_KEYS.has(label),
    }))
    .sort((a, b) =>
      a.muted === b.muted ? b.value - a.value : a.muted ? 1 : -1,
    );
}

/**
 * The day-only `start`/`end` a trend response actually covers, read from its
 * raw ISO instants (e.g. `"2026-08-01T00:00:00+02:00"` -> `"2026-08-01"`).
 * The backend resolves and echoes both in the application timezone (Europe/
 * Amsterdam) — the same timezone its day buckets use — so slicing the string
 * is exact regardless of which timezone the browser runs in. Building the
 * zero-fill range from the request's own params instead would use the
 * browser's "today" for an omitted `end`, which can disagree with the
 * backend's resolved `end` by a day for a user outside that timezone, or
 * near midnight for anyone.
 *
 * Returns `undefined` when the payload doesn't carry the two ISO strings, so
 * `toTrendPoints` falls through to its own empty result rather than crashing.
 */
export function toTrendRange(
  payload: unknown,
): Pick<AnalyticsParams, "start" | "end"> | undefined {
  if (payload === null || typeof payload !== "object") {
    return undefined;
  }
  const { start, end } = payload as { start?: unknown; end?: unknown };
  if (typeof start !== "string" || typeof end !== "string") {
    return undefined;
  }
  return { start: start.slice(0, 10), end: end.slice(0, 10) };
}

/**
 * Turns raw breakdown entries into a chronological, zero-filled trend: every
 * calendar day from `range.start` to `range.end` (inclusive), in order, missing
 * days filled with 0. `range` should come from {@link toTrendRange} (the
 * response's own resolved dates), not from the request params — see its own
 * comment for why.
 *
 * Two things this deliberately does NOT show: the not-recorded bucket
 * (`"-"`/`""` — dossiers with no damage date at all is not a day of damage),
 * and any day outside `range` — `dossiersPerDamageDate`'s own contract allows
 * one, since the endpoint filters on the dossier's creation date rather than
 * on the damage date, but plotting it next to the requested days would draw
 * it as though it were adjacent to them, which it may not be.
 */
export function toTrendPoints(
  entries: readonly { label: string; count: number }[],
  range: Pick<AnalyticsParams, "start" | "end"> | undefined,
): ChartTimeSeriesPoint[] {
  const start = parseISODate(range?.start ?? "");
  const end = parseISODate(range?.end ?? "");
  if (start === null || end === null) {
    return [];
  }

  const counts = new Map(
    entries
      .filter(({ label }) => !NOT_RECORDED_KEYS.has(label))
      .map(({ label, count }) => [label, count]),
  );

  const points: ChartTimeSeriesPoint[] = [];
  for (
    let day = start;
    day.getTime() <= end.getTime();
    day = subCalendarDays(day, -1)
  ) {
    const key = toISODate(day);
    points.push({ label: key, value: counts.get(key) ?? 0 });
  }
  return points;
}

/**
 * Whether the value is the photos-per-dossier row shape
 * (`PhotosPerDossierItemOutput`: `{ photoCount, dossierCount }`). Structural
 * check rather than `instanceof`: the generated types are interfaces.
 */
function isPhotosPerDossierItem(
  value: unknown,
): value is PhotosPerDossierItemOutput {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof (value as PhotosPerDossierItemOutput).photoCount === "number" &&
    typeof (value as PhotosPerDossierItemOutput).dossierCount === "number"
  );
}

/**
 * Buckets the photos-per-dossier rows (`{ photoCount, dossierCount }`,
 * ascending by photo count) into chart segments: "Geen foto's" for 0, "1
 * foto" for 1, "N foto's" beyond. One segment per photo count — the endpoint
 * deliberately does not group into ranges (`PhotosPerDossierResource`: where
 * those boundaries sit differs per client and per chart), and the prototype
 * renders the raw counts. Returns [] on a payload that is not the row list,
 * so the chart renders its empty state rather than throwing.
 */
export function toPhotoCountSegments(payload: unknown): ChartSegment[] {
  if (payload === null || typeof payload !== "object") {
    return [];
  }
  const rows = (payload as { photosPerDossier?: unknown }).photosPerDossier;
  if (!Array.isArray(rows) || !rows.every(isPhotosPerDossierItem)) {
    return [];
  }

  return rows.map(({ photoCount, dossierCount }) => ({
    label:
      photoCount === 0
        ? "Geen foto's"
        : photoCount === 1
          ? "1 foto"
          : `${photoCount} foto's`,
    value: dossierCount,
  }));
}

/**
 * One tile of the Reporting KPI row: a period-aware `StatCard` backed by a
 * `data-analytics` endpoint. Which tiles render and in what order is decided
 * by the served configuration's `kpis` list — the backend's resolver already
 * dropped every tile the current user may not read, so no tile ever fires a
 * request the user cannot make.
 *
 * `unit: "count"` tiles render a plain number and compare against the
 * comparison period with `toPercentageDelta`; `unit: "percentage"` tiles
 * render a percentage and compare in percentage POINTS (the prototype is
 * explicit: a percentage of a percentage is noise).
 *
 * `media-intent-per-day`, `has-images` and `has-repair-appointment` are also
 * rendered as charts inside tabs — same endpoint, same params, so React Query
 * deduplicates; do not "optimise" that with shared state.
 */
export interface AnalyticsKpiTile {
  readonly label: string;
  readonly icon: typeof FolderOpen;
  readonly unit: "count" | "percentage";
  /**
   * The page-side call shape: range params in, the query result out. The
   * comparison-range second query passes `options.query.enabled`, which the
   * exact generated-hook signature covers — keep `useQuery` on that signature
   * (not the narrowed `AnalyticsQueryHook` alias) so the enabled gate types.
   */
  readonly useQuery: (
    params: AnalyticsParams,
    options?: {
      readonly query?: { readonly enabled?: boolean };
      readonly request?: AnalyticsRequestOptions;
    },
  ) => AnalyticsQueryResult<unknown>;
  /** Extracts the tile's value from the endpoint payload, if it is there. */
  readonly toValue: (payload: unknown) => number | undefined;
}

/**
 * Sums the per-day counts of a `media-intent-per-day` response
 * (`{ start, end, dayToCount }`). Every key is a `YYYY-MM-DD` day:
 * `AnalyticsBucketMapper::mapPerDay()` emits exactly the days of the
 * requested period and rejects null or out-of-range days, so there is no
 * not-recorded bucket to skip here. Returns `undefined` for any other payload
 * so the tile falls back to its empty/loading state rather than rendering a
 * raw object.
 */
export function toDayCountTotal(payload: unknown): number | undefined {
  if (payload === null || typeof payload !== "object") {
    return undefined;
  }
  const dayToCount = (payload as { dayToCount?: unknown }).dayToCount;
  if (
    dayToCount === null ||
    typeof dayToCount !== "object" ||
    Array.isArray(dayToCount) ||
    !Object.values(dayToCount).every((count) => typeof count === "number")
  ) {
    return undefined;
  }

  return Object.values(dayToCount).reduce((total, count) => total + count, 0);
}

/**
 * The `yes / (yes + no)` percentage of a yes/no breakdown response
 * (`has-images`, `has-repair-appointment`). Returns `undefined` when the
 * breakdown is missing, not a yes/no map, or empty — a percentage of nothing
 * is not `0%`, it is "no data", and the tile must not render a plausible 0.
 */
export function toYesPercentage(payload: unknown): number | undefined {
  const entries = toBreakdownEntries(payload);
  const yes = entries.find((entry) => entry.label === "yes")?.count ?? 0;
  const no = entries.find((entry) => entry.label === "no")?.count ?? 0;
  if (entries.length === 0 || yes + no === 0) {
    return undefined;
  }
  return Math.round((yes / (yes + no)) * 100);
}

/**
 * The tiles the KPI row can render, keyed by `ReportingKpi` id. The row is
 * configuration-driven: the served layout's `kpis` list decides which tiles
 * render and in what order, so `photosReceived` staying here is what lets a
 * client add it back without a code change — our fixtures deliberately do
 * not configure it.
 */
export const ANALYTICS_KPIS: Record<ReportingKpi, AnalyticsKpiTile> = {
  dossiers: {
    label: "Dossiers",
    icon: FolderOpen,
    unit: "count",
    useQuery: useGetDataAnalyticsTotalDossiers,
    toValue: toTotalDossiersCount,
  },
  photosReceived: {
    label: "Schadefoto's ontvangen",
    icon: FileImage,
    unit: "count",
    useQuery: (params, options) =>
      useGetDataAnalyticsMediaIntentPerDay(
        { ...params, intent: "damage_overview" },
        options,
      ),
    toValue: toDayCountTotal,
  },
  withPhotos: {
    label: "Met schadefoto's",
    icon: Camera,
    unit: "percentage",
    useQuery: useGetDataAnalyticsHasImages,
    toValue: toYesPercentage,
  },
  withRepairAppointment: {
    label: "Met herstelafspraak",
    icon: CalendarCheck,
    unit: "percentage",
    useQuery: useGetDataAnalyticsHasRepairAppointment,
    toValue: toYesPercentage,
  },
};
